import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

export const fetchAllUsers = createAsyncThunk(
  "userState/fetchAllUsers",
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const {
        searchText,
        id = 1,
        phoneDial,
        gurudwaraId,
        categoryId,
        Location,
        Sorting,
        caId,
      } = params; // Default page to 1
      const response = await axiosClient.get(`setting/getAllProfile/${id}`, {
        params: {
          searchText: searchText || undefined,
          phoneDial: phoneDial || undefined,
          gurudwaraId: gurudwaraId || undefined,
          categoryId: categoryId || undefined,
          Location: Location || undefined,
          Sorting: Sorting || undefined,
          caId: caId || undefined,
        },
      });
      const fetchedUsers = response.data.Users || [];
      const totalCount = response.data.total || fetchedUsers.length;
      const serializedUsers = Array.isArray(fetchedUsers)
        ? fetchedUsers.map((user) => ({ ...user }))
        : [];
      dispatch(
        setUsersData({
          users: serializedUsers,
          totalCount,
        })
      );
      dispatch(setLoadingUsers(false));

      return fetchedUsers;
    } catch (err) {
      console.error("Error fetching users:", err);
      dispatch(
        setError(err.response?.data?.message || "Failed to fetch users")
      );
      dispatch(setLoadingUsers(false));
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);
export const updateUserStatus = createAction(
  "userState/updateUserStatus",
  (userId, caIdStatus, caIdComment = "") => ({
    payload: { userId, caIdStatus, caIdComment },
  })
);
export const fetchPendingCaid = createAsyncThunk(
  "userState/fetchPendingCaid",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClient.get("setting/PendingCaid");
      const result = response.data.Data || 0;

      dispatch(setPendingCaidCount(result));
      dispatch(setLoadingPendingCaid(false));
      return result;
    } catch (err) {
      console.error("Error fetching pending CAID count:", err);
      dispatch(
        setError(
          err.response?.data?.message || "Failed to fetch pending CAID count"
        )
      );
      dispatch(setLoadingPendingCaid(false));
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch pending CAID count"
      );
    }
  }
);

const initialState = {
  finalUsers: [],
  loadingUsers: false,
  loadingPendingCaid: false,
  extRequestFilter: false,
  selectedGurdwara: 0,
  pendingCaidCount: 0,
  usersCount: 0,
  currentUsersPage: 1,
  searchUserQuery: "",
  error: null,
  userSearchText: "",
  sortBy: "",
};

const userDataSlice = createSlice({
  name: "userState",
  initialState,
  reducers: {
    setUsersData: (state, action) => {
      state.finalUsers = action.payload.users;
      state.usersCount = action.payload.totalCount;
    },
    setLoadingUsers: (state, action) => {
      state.loadingUsers = action.payload;
    },
    setLoadingPendingCaid: (state, action) => {
      state.loadingPendingCaid = action.payload;
    },
    setExtRequestFilter: (state, action) => {
      state.extRequestFilter = action.payload;
    },
    setSelectedGurdwara: (state, action) => {
      state.selectedGurdwara = action.payload;
    },
    setPendingCaidCount: (state, action) => {
      state.pendingCaidCount = action.payload;
    },
    setUsersCount: (state, action) => {
      state.usersCount = action.payload;
    },
    setCurrentUsersPage: (state, action) => {
      state.currentUsersPage = action.payload;
    },
    setSearchUserQuery: (state, action) => {
      state.searchUserQuery = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setUserSearchText: (state, action) => {
      state.userSearchText = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loadingUsers = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state) => {
        state.loadingUsers = false;
        state.error = null;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loadingUsers = false;
        state.error = action.payload;
      })
      .addCase(fetchPendingCaid.pending, (state) => {
        state.loadingPendingCaid = true;
        state.error = null;
      })
      .addCase(fetchPendingCaid.fulfilled, (state) => {
        state.loadingPendingCaid = false;
        state.error = null;
      })
      .addCase(fetchPendingCaid.rejected, (state, action) => {
        state.loadingPendingCaid = false;
        state.error = action.payload;
      });
    builder.addCase(updateUserStatus, (state, action) => {
      const { userId, caIdStatus, caIdComment } = action.payload;
      state.finalUsers = state.finalUsers.map((user) =>
        user.Id === userId ? { ...user, caIdStatus, caIdComment } : user
      );
    });
  },
});

export const {
  setUsersData,
  setLoadingUsers,
  setLoadingPendingCaid,
  setExtRequestFilter,
  setSelectedGurdwara,
  setPendingCaidCount,
  setUsersCount,
  setCurrentUsersPage,
  setSearchUserQuery,
  setError,
  clearError,
  setSortBy,
  setUserSearchText,
} = userDataSlice.actions;

export default userDataSlice.reducer;
